import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@airbus/components-react/components/Modal'
import Button from '@airbus/components-react/components/Button'
import LightDarkContext from 'context/LightDarkModeContext'
import PageHeader from 'components/pageHeader'
import { Container } from '@airbus/components-react/components'
import { FormProvider, useForm } from 'react-hook-form'
import { Inline } from '@airbus/components-react/components'
import FormInput from 'components/FormFields/FormInput'
import useMutation from 'utils/hooks/useMutation'
import urlApis from 'utils/api/urlApis'

type Props = {
  title: string
  label: string
  lang: string
  idCommand: string
  isReasonCompulsory: boolean
  isOpened: boolean
  onCloseModal: () => void
  actionName: string
  onUserActionCompleted: (action: string) => void
}

type Inputs = {
  reason: string
}

const styleModal = {
  position: 'absolute',
  transform: 'translate(50%, 50%)',
  width: '50%',
  height: '13em',
  border: '2px solid #000',
  boxShadow: 24,
  overflowY: 'scroll',
  scrollbarWidth: 'none',
}

/**
 * Modal window which prompts for a reason (or whatsoever, simply said an input field) and returns the input to the caller via its onSubmitReason handler
 * @param {*} props
 * @returns the input to the caller via its onSubmitReason handler
 */
const ModalAskReason = ({
  title,
  label,
  lang,
  idCommand,
  isReasonCompulsory,
  isOpened,
  onCloseModal,
  actionName,
  onUserActionCompleted,
}: Props) => {
  const { t } = useTranslation()

  const methods = useForm<Inputs>({
    defaultValues: { reason: '' },
  })

  const { mutate: rejectOrder } = useMutation<Inputs>(urlApis.URL_ORDER_REFUSE_PUT, '', [], false)
  const [isLoading, setIsLoading] = useState(false)
  const { handleSubmit, resetField } = methods

  const handleSubmitReject = async (data: Inputs) => {
    setIsLoading(true)
    try {
      await rejectOrder({ data: {}, params: { lang: lang, cdnumdoc: idCommand, reason: data.reason }, method: 'PUT' })
      onUserActionCompleted(actionName)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    resetField('reason')
  }, [isOpened])

  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <Modal
      open={isOpened}
      onClose={onCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
        <>
          <PageHeader
            pageTitle={title}
            modalEmbedded={true}
            modalOnClose={onCloseModal}
            style={{ top: 0 }}
            localLoading={isLoading}
          />

          <Container style={{ flex: 1 }} fixed>
            <div style={{ padding: '1em' }}>
              <FormProvider {...methods}>
                <form style={{ marginTop: '1em' }} onSubmit={handleSubmit(handleSubmitReject)}>
                  <FormInput name='reason' rules={{ required: isReasonCompulsory }} label={label} maxLength={90} />

                  <div style={{ width: '100%' }}>
                    <Inline spacing='1-x' style={{ margin: '10px 0' }}>
                      <Button variant='secondary' tabIndex={0} onClick={onCloseModal}>
                        {t('8000569')}
                      </Button>
                      <Button
                        variant='primary'
                        type='submit'
                        tabIndex={0}
                        disabled={!methods.formState.isDirty || isLoading}
                      >
                        {t('8000202')}
                      </Button>
                    </Inline>
                  </div>
                </form>
              </FormProvider>
            </div>
          </Container>
        </>
      </div>
    </Modal>
  )
}

export default ModalAskReason
